// src/services/uploadService.js
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase/config';

// Configuration Cloudinary
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

/**
 * Upload un fichier - Route automatiquement vers le bon service
 * Images → Cloudinary (optimisées, CDN)
 * PDF → Firebase Storage
 */
export const uploadFile = async (file, folder = 'documents') => {
  const isImage = file.type.startsWith('image/');
  const isPDF = file.type === 'application/pdf';
  
  if (!isImage && !isPDF) {
    throw new Error('Type de fichier non supporté. Utilisez des images ou des PDF.');
  }
  
  // Vérifier la taille (max 10MB pour PDF, 5MB pour images)
  const maxSize = isPDF ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error(`Fichier trop volumineux (max ${maxSize / (1024 * 1024)}MB)`);
  }
  
  if (isImage) {
    return uploadToCloudinary(file, folder);
  } else {
    return uploadToFirebase(file, folder);
  }
};

/**
 * Upload vers Cloudinary (images)
 */
const uploadToCloudinary = async (file, folder) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', folder);
  
  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    {
      method: 'POST',
      body: formData
    }
  );
  
  if (!response.ok) {
    throw new Error('Erreur lors de l\'upload de l\'image');
  }
  
  const data = await response.json();
  
  return {
    id: data.public_id,
    nom: file.name,
    type: 'image',
    service: 'cloudinary',
    url: data.secure_url,
    thumbnailUrl: data.secure_url.replace('/upload/', '/upload/w_200,h_200,c_thumb/'),
    taille: file.size,
    uploadDate: new Date().toISOString()
  };
};

/**
 * Upload vers Firebase Storage (PDF)
 */
const uploadToFirebase = async (file, folder) => {
  const timestamp = Date.now();
  const fileName = `${folder}/${timestamp}_${file.name}`;
  const storageRef = ref(storage, fileName);
  
  const snapshot = await uploadBytes(storageRef, file);
  const url = await getDownloadURL(snapshot.ref);
  
  return {
    id: `doc_${timestamp}`,
    nom: file.name,
    type: 'pdf',
    service: 'firebase',
    url: url,
    path: fileName,
    taille: file.size,
    uploadDate: new Date().toISOString()
  };
};

/**
 * Upload multiple fichiers
 */
export const uploadMultipleFiles = async (files, folder = 'documents', onProgress = null) => {
  const uploads = [];
  const errors = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    if (onProgress) {
      onProgress({
        current: i + 1,
        total: files.length,
        percentage: Math.round(((i + 1) / files.length) * 100),
        fileName: file.name
      });
    }
    
    try {
      const result = await uploadFile(file, folder);
      uploads.push(result);
    } catch (error) {
      console.error(`Erreur upload ${file.name}:`, error);
      errors.push({ fileName: file.name, error: error.message });
    }
  }
  
  return { success: uploads, errors };
};

/**
 * Supprime un fichier
 */
export const deleteFile = async (fileData) => {
  if (fileData.service === 'firebase' && fileData.path) {
    const storageRef = ref(storage, fileData.path);
    await deleteObject(storageRef);
  }
  // Pour Cloudinary, on ne fait rien (suppression manuelle dans le dashboard si besoin)
  return true;
};